/**
 * Shopify Newsletter Subscription API
 * Functions for newsletter subscription using the Shopify Admin API via server endpoints
 */

interface SubscriptionData {
  id: string;
  email: string;
}

interface SubscribeResponse {
  success: boolean;
  message: string;
  data?: SubscriptionData;
}

/**
 * Subscribe an email to the newsletter
 *
 * This function uses a server-side API endpoint that connects to the Shopify Admin API
 * to properly subscribe a user to marketing emails.
 *
 * @param email The email address to subscribe
 * @returns A response indicating success or failure
 */
export const subscribe = async (email: string): Promise<SubscribeResponse> => {
  try {
    if (!email) {
      return {
        success: false,
        message: "Email is required",
      };
    }

    const response = await fetch("/api/newsletter/subscribe", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data.message || "Failed to subscribe",
      };
    }

    return {
      success: true,
      message: "Subscription successful",
    };
  } catch (error) {
    console.error("Newsletter subscription error:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to subscribe",
    };
  }
};
